import { useCallback, useEffect, useState } from "react";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LiveRoomPanel from "@/components/LiveRoomPanel";
import RecordingControls from "@/components/RecordingControls";
import WebinarQrCode from "@/components/WebinarQrCode";

export default function Webinars() {
  const { projectId } = useWorkspace();
  const [items, setItems] = useState([]), [title, setTitle] = useState(""), [scheduled, setScheduled] = useState("");
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [selected, setSelected] = useState(null), [mediaSession, setMediaSession] = useState(null);
  const load = useCallback(async () => {
    if (!projectId) return;
    try { const { data } = await api.get(`/dashboard/projects/${projectId}/webinars`); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, [projectId]);
  useEffect(() => { setSelected(null); void load(); }, [load]);
  const create = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try { await api.post(`/dashboard/projects/${projectId}/webinars`, { title, scheduled_at: scheduled ? new Date(scheduled).toISOString() : null }); setTitle(""); setScheduled(""); await load(); }
    catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const action = async (item, command) => {
    setBusy(true); setError("");
    try { const { data } = await api.post(`/dashboard/webinars/${item.id}/${command}`); if (command === "start") setSelected(data); if (["end", "cancel"].includes(command)) setSelected(null); await load(); }
    catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  if (!projectId) return <p>Sélectionnez un projet pour continuer.</p>;
  return <div className="space-y-5">
    <div><h1 className="text-2xl font-semibold">Webinaires</h1><p className="text-sm text-zinc-400">Événements professionnels avec invités sans compte, soumis aux options du contrat.</p></div>
    {error && <p role="alert" className="text-red-400">{error}</p>}
    <form onSubmit={create} className="space-y-3 rounded border border-zinc-800 p-4">
      <label className="block text-sm">Titre<Input required maxLength={160} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      <label className="block text-sm">Date locale (laisser vide pour programmer ultérieurement)<Input type="datetime-local" value={scheduled} onChange={(e) => setScheduled(e.target.value)} /></label>
      <Button disabled={busy || !title.trim()}>Créer le webinaire</Button>
    </form>
    {selected && <LiveRoomPanel roomId={selected.id} tokenEndpoint={`/dashboard/webinars/${selected.id}/token`} onSession={setMediaSession} />}
    {selected && <RecordingControls kind="webinars" resourceId={selected.id} mediaSession={mediaSession} />}
    <ul className="space-y-3">{items.map((item) => <li key={item.id} className="space-y-2 rounded border border-zinc-800 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2"><span className="font-medium">{item.title}</span><span className="text-xs text-zinc-400">{item.status}</span></div>
      {item.scheduled_at && <p className="text-xs text-zinc-500">{new Date(item.scheduled_at).toLocaleString()}</p>}
      <p className="break-all text-xs text-zinc-500">Lien invité : {`${window.location.origin}/webinar/${item.slug}`}</p>
      <WebinarQrCode webinarId={item.id} fallbackUrl={`${window.location.origin}/webinar/${item.slug}`} />
      <div className="flex flex-wrap gap-2">
        {item.status === "scheduled" && <Button disabled={busy} onClick={() => action(item, "start")}>Démarrer</Button>}
        {item.status === "live" && <Button variant="outline" disabled={busy} onClick={() => setSelected(item)}>Ouvrir la régie</Button>}
        {["scheduled", "live"].includes(item.status) && <Button variant="outline" disabled={busy} onClick={() => action(item, item.status === "live" ? "end" : "cancel")}>{item.status === "live" ? "Terminer" : "Annuler"}</Button>}
        <Button variant="outline" disabled={busy} onClick={() => action(item, "duplicate")}>Dupliquer</Button>
      </div>
    </li>)}</ul>
    {!items.length && <p className="text-sm text-zinc-400">Aucun webinaire pour ce projet.</p>}
  </div>;
}
